/**
 * A6 crash and error monitoring (in-stack, no third-party vendor).
 * Uncaught errors, unhandled promise rejections and React render crashes are sent to the
 * `cp_report_client_error` RPC (signed-in users only; readable only by the platform owner).
 * Never sends request bodies, form values or tokens: only the error message, a truncated stack,
 * the route path (no query string) and the app version. Reporting failures are swallowed.
 */
import { supabase } from "../supabase";
import { APP_VERSION } from "../productEducation/config/appRelease";

const MAX_PER_SESSION = 20;
const DEDUPE_MS = 30000;
let sent = 0;
const recent = new Map();
let installed = false;

function routePath() {
  try {
    return window.location.pathname;
  } catch {
    return null;
  }
}

const SCRUBBERS = [
  [/eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}(\.[A-Za-z0-9_-]*)?/g, "[jwt]"],
  [/bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [redacted]"],
  [/(sk|pk|rk)[-_](live|test|proj)?[-_]?[A-Za-z0-9]{12,}/g, "[key]"],
  [/(apikey|api_key|access_token|refresh_token|token|password|secret|signature)=[^&\s"']+/gi, "$1=[redacted]"],
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]"],
  [/[A-Fa-f0-9]{32,}/g, "[hex]"],
];

/** Same rules as the server (cp_scrub_diagnostic_text); the server scrubs again. */
export function scrubDiagnosticText(text) {
  if (text == null) return text;
  return SCRUBBERS.reduce((acc, [re, rep]) => acc.replace(re, rep), String(text));
}

export function normalizeError(errorLike) {
  if (errorLike instanceof Error) {
    return { message: errorLike.message || errorLike.name || "Error", stack: errorLike.stack || null };
  }
  if (typeof errorLike === "string") return { message: errorLike, stack: null };
  try {
    return { message: JSON.stringify(errorLike) || "Unknown error", stack: null };
  } catch {
    return { message: String(errorLike), stack: null };
  }
}

export async function reportClientError(kind, errorLike, { client = supabase, now = Date.now() } = {}) {
  try {
    const { message, stack } = normalizeError(errorLike);
    const key = `${kind}:${message}`;
    if (recent.has(key) && now - recent.get(key) < DEDUPE_MS) return false;
    if (sent >= MAX_PER_SESSION) return false;
    const { data: sessionData } = await client.auth.getSession();
    if (!sessionData?.session) return false;
    recent.set(key, now);
    sent += 1;
    const { data, error } = await client.rpc("cp_report_client_error", {
      p_kind: kind,
      p_message: scrubDiagnosticText(String(message)).slice(0, 1000),
      p_stack: stack ? scrubDiagnosticText(String(stack)).slice(0, 4000) : null,
      p_route: routePath(),
      p_release: APP_VERSION || null,
      p_user_agent: typeof navigator !== "undefined" ? String(navigator.userAgent || "").slice(0, 300) : null,
      p_organization_id: null,
    });
    return !error && data === true;
  } catch {
    return false;
  }
}

export function installGlobalErrorReporting(target = window) {
  if (installed || !target?.addEventListener) return;
  installed = true;
  target.addEventListener("error", (event) => {
    reportClientError("error", event?.error || event?.message || "Script error");
  });
  target.addEventListener("unhandledrejection", (event) => {
    reportClientError("unhandledrejection", event?.reason || "Unhandled rejection");
  });
}

export function __resetErrorReporterForTests() {
  sent = 0;
  recent.clear();
  installed = false;
}
